// The checkout's Price table as the agent reads it - QA doc docs/features/iway-transfers/qa.md R-HT22 (Amiran 09.10.2026: the amounts shown must add
// up to the cent). The server prices in the package currency and the price pipe converts each figure on its own, so a Total converted as one
// number can be a cent off the converted rows above it, and a Transfers row converted as one number a cent off its cars (54.09 + 54.09 shown as
// 108.17). Each row is converted and rounded first; the Transfers row is the sum of its legs as shown when the legs are its lines; the Total row
// is the sum of the shown rows.
import { IShownTarget, percentOf, roundCents, sumCents } from '@shared/services/shown-money.service';
import { CheckoutPriceItem } from './checkout.types';

/**
 * Rows in the shown currency. `transferLegs` = the price of each chosen transfer line in the package currency (one RFTransferService line per
 * leg, packages-api CheckoutService.CreateRFTransfer); used only when they ARE the Transfers row (their sum equals it to the cent).
 */
export const shownPriceRows = (
  rows: readonly CheckoutPriceItem[],
  target: IShownTarget,
  transferLegs: readonly number[] = []
): CheckoutPriceItem[] => {
  const shown = (amount: number | null | undefined) => roundCents(Number(amount ?? 0) * target.rate);
  const converted = rows.map((row) => {
    let totalPrice = shown(row.totalPrice);
    let agentCommissionAmount = shown(row.agentCommissionAmount);
    // the legs ARE the row when their sum equals it to the cent (half a cent of tolerance for float noise)
    const legsAreTheRow = Math.abs(sumCents(transferLegs) - roundCents(row.totalPrice)) < 0.005;
    if (row.type === 'Transfers' && transferLegs.length && legsAreTheRow) {
      totalPrice = sumCents(transferLegs.map((leg) => shown(leg)));
      agentCommissionAmount = sumCents(transferLegs.map((leg) => shown(percentOf(leg, row.agentCommissionPercent))));
    }
    return {
      ...row,
      currency: target.currency,
      totalPrice,
      agentCommissionAmount,
      amountToPay: roundCents(totalPrice - agentCommissionAmount)
    };
  });
  const parts = converted.filter((row) => row.type !== 'Total');
  if (parts.length < 2) return converted;
  const totalPrice = sumCents(parts.map((row) => row.totalPrice));
  const agentCommissionAmount = sumCents(parts.map((row) => row.agentCommissionAmount));
  return converted.map((row) =>
    row.type === 'Total'
      ? { ...row, totalPrice, agentCommissionAmount, amountToPay: roundCents(totalPrice - agentCommissionAmount) }
      : row
  );
};
